import type { MessengerClient } from '@sd/client-core';
import type { Person, Reaction } from '@sd/contracts';
import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { Icon } from '../icons/Icon';
import { Avatar } from '../ui/Avatar';
import { isComposing } from '../ui/keys';
import { LoadFailed } from './bits';
import { reactionLabel, reactorNames, type PersonLookup } from './copy';
import { EMOJI_GROUPS, QUICK_REACTIONS, emojiEntry, emojiInGroup, searchEmoji, type EmojiGroupKey } from './emoji';

interface ChipsProps {
  reactions: readonly Reaction[];
  people: PersonLookup;
  meId: string;
  /** Chips toggle and the add button shows; not in read-only conversations or on servers without reactions. */
  canReact: boolean;
  /** Roving tabindex: reached with Tab only while the message is the timeline's stop. */
  tabbable: boolean;
  onToggle(emoji: string): void;
  onAdd(anchor: HTMLElement): void;
}

/**
 * A pointer click leaves focus where it was (the input, usually). Focusing the chip would show
 * the row's menu button, which in the stacked layout below 360 px pushes the chip down between
 * mousedown and mouseup, so the click would land elsewhere.
 */
const keepFocus = (event: ReactMouseEvent) => event.preventDefault();

/** Chips under a message: emoji and count, highlighted when the viewer is among them; who reacted on hover and focus. */
export function ReactionChips({ reactions, people, meId, canReact, tabbable, onToggle, onAdd }: ChipsProps) {
  const id = useId();
  if (!reactions.length) return null;
  return (
    <div className="rx-row" role="group" aria-label="반응">
      {reactions.map((reaction, index) => (
        <Fragment key={reaction.emoji}>
          <button
            type="button"
            className="rx"
            aria-pressed={reaction.me}
            aria-label={reactionLabel(reaction)}
            aria-describedby={`${id}-${index}`}
            aria-disabled={canReact ? undefined : true}
            data-tip={reactorNames(reaction, people, meId, 3)}
            tabIndex={tabbable ? 0 : -1}
            onMouseDown={keepFocus}
            onClick={() => canReact && onToggle(reaction.emoji)}
          >
            <span className="rx-e" aria-hidden="true">
              {reaction.emoji}
            </span>
            <span aria-hidden="true">{reaction.count}</span>
          </button>
          <span id={`${id}-${index}`} className="sr">
            {reactorNames(reaction, people, meId)}
          </span>
        </Fragment>
      ))}
      {canReact ? (
        <button type="button" className="rx add" aria-label="반응 추가" data-tip="반응 추가" aria-haspopup="dialog" tabIndex={tabbable ? 0 : -1} onMouseDown={keepFocus} onClick={(e) => onAdd(e.currentTarget)}>
          <Icon name="smile" size="sm" />
        </button>
      ) : null}
    </div>
  );
}

const COLUMNS = 8;
type Tab = 'quick' | EmojiGroupKey;
const TABS: ReadonlyArray<{ key: Tab; label: string }> = [{ key: 'quick', label: '자주 쓰는 반응' }, ...EMOJI_GROUPS];

/**
 * Popover content (role dialog "반응 추가"): search, category tabs and one grid of emoji.
 * Arrow keys move in the tabs and the grid, ↓ from the search field goes into the grid,
 * Enter in it picks the first result. Emoji the viewer already reacted with are pressed:
 * picking one takes it back, like its chip.
 */
export function ReactionPicker({ mine, onPick }: { mine: readonly string[]; onPick(emoji: string): void }) {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<Tab>('quick');
  const [focus, setFocus] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const gridId = useId();
  const searching = query.trim() !== '';
  const items = useMemo(() => (searching ? searchEmoji(query) : tab === 'quick' ? QUICK_REACTIONS.map(emojiEntry) : emojiInGroup(tab)), [searching, query, tab]);
  const current = Math.min(focus, Math.max(0, items.length - 1));

  const focusItem = (index: number) => {
    setFocus(index);
    grid.current?.querySelectorAll<HTMLButtonElement>('button')[index]?.focus();
  };
  const onGridKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const index = [...(grid.current?.querySelectorAll('button') ?? [])].indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    const moves: Record<string, number> = { ArrowLeft: index - 1, ArrowRight: index + 1, ArrowUp: index - COLUMNS, ArrowDown: index + COLUMNS, Home: 0, End: items.length - 1 };
    const target = moves[event.key];
    if (target === undefined) return;
    event.preventDefault();
    // Up from the first row goes back to the search field.
    if (event.key === 'ArrowUp' && target < 0) input.current?.focus();
    else focusItem(Math.max(0, Math.min(items.length - 1, target)));
  };
  const onTabKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const index = TABS.findIndex((t) => t.key === tab);
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = TABS[(index + step + TABS.length) % TABS.length]!;
    setTab(next.key);
    setFocus(0);
    event.currentTarget.querySelector<HTMLElement>(`[data-tab="${next.key}"]`)?.focus();
  };

  return (
    <div className="rx-pick">
      <label className="search-pill rx-search">
        <Icon name="search" size="sm" />
        <input
          ref={input}
          type="search"
          aria-label="이모지 검색"
          placeholder="이모지 검색 · 예: 축하, ㅊㅎ"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setFocus(0);
          }}
          onKeyDown={(e) => {
            if (isComposing(e)) return;
            if (e.key === 'ArrowDown' && items.length) {
              e.preventDefault();
              focusItem(0);
            } else if (e.key === 'Enter' && searching && items[0]) {
              e.preventDefault();
              onPick(items[0].emoji);
            }
          }}
        />
      </label>
      {searching ? null : (
        <div className="rx-tabs" role="tablist" aria-label="이모지 분류" onKeyDown={onTabKey}>
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              data-tab={t.key}
              aria-selected={tab === t.key}
              aria-controls={gridId}
              aria-label={t.label}
              data-tip={t.label}
              data-tip-pos="below"
              tabIndex={tab === t.key ? 0 : -1}
              onClick={() => {
                setTab(t.key);
                setFocus(0);
              }}
            >
              {t.key === 'quick' ? <Icon name="sparkle" size="sm" /> : <span aria-hidden="true">{EMOJI_GROUPS.find((g) => g.key === t.key)!.face}</span>}
            </button>
          ))}
        </div>
      )}
      <div ref={grid} id={gridId} className="rx-grid" role="group" aria-label={searching ? '검색 결과' : TABS.find((t) => t.key === tab)!.label} onKeyDown={onGridKey}>
        {items.map((entry, index) => (
          <button
            key={entry.emoji}
            type="button"
            aria-label={entry.name}
            aria-pressed={mine.includes(entry.emoji)}
            tabIndex={index === current ? 0 : -1}
            onFocus={() => setFocus(index)}
            onClick={() => onPick(entry.emoji)}
          >
            {entry.emoji}
          </button>
        ))}
      </div>
      {searching && !items.length ? <p className="rx-none">찾는 이모지가 없어요</p> : null}
      <div className="sr" role="status">
        {searching ? `이모지 ${items.length}개` : ''}
      </div>
    </div>
  );
}

/** Rendered in the popover layer (outside the client context), so the client is passed in. */
export function ReactorsPopover({ client, conversationId, messageId, meId }: { client: MessengerClient; conversationId: string; messageId: string; meId: string }) {
  const [data, setData] = useState<Array<{ emoji: string; people: Person[] }> | 'failed' | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    client
      .reactors(conversationId, messageId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData('failed'));
    return () => {
      alive = false;
    };
  }, [client, conversationId, messageId, attempt]);
  if (data === 'failed')
    return (
      <LoadFailed
        onRetry={() => {
          setData(null);
          setAttempt((n) => n + 1);
        }}
      />
    );
  if (!data) return <h2>불러오는 중…</h2>;
  if (!data.length) return <h2>반응이 없어요</h2>;
  return (
    <div className="rx-who">
      {data.map((reaction, index) => (
        <section key={reaction.emoji} aria-label={`${reaction.emoji} ${reaction.people.length}명`}>
          {index ? <hr /> : null}
          <h2>
            {reaction.emoji} {reaction.people.length}
          </h2>
          {reaction.people.map((person) => (
            <div key={person.id} className="pi">
              <Avatar person={person} noDot />
              {person.displayName}
              {person.id === meId ? <small>나</small> : person.status === 'deactivated' ? <small>비활성화됨</small> : null}
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
